import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LandingNavigation from '../components/LandingNavigation';
import LandingFooter from '../components/LandingFooter';
import '../styles/landing.css';

const pricingTiers = [
    {
        name: 'Starter',
        price: 'TBA',
        description: 'Perfect for individual creators and small teams getting started.',
        features: [
            'AI Creative Generation',
            'Basic Analytics Dashboard',
            '1 Brand Workspace',
            '50 Creatives per month',
            'Content Scheduler',
            'Email Support',
        ],
        cta: 'Join Waitlist',
        highlighted: false,
    },
    {
        name: 'Professional',
        price: 'TBA',
        description: 'For growing marketing teams who need more power and flexibility.',
        features: [
            'Everything in Starter',
            'Advanced AI Insights',
            '5 Brand Workspaces',
            'Unlimited Creatives',
            'Visual Funnel Builder',
            'Team Collaboration',
            'Priority Support',
            'API Access',
        ],
        cta: 'Join Waitlist',
        highlighted: true,
    },
    {
        name: 'Enterprise',
        price: 'Contact Us',
        description: 'Custom solutions for large teams and agencies.',
        features: [
            'Everything in Professional',
            'Unlimited Brands',
            'Custom Integrations',
            'Dedicated Account Manager',
            'SLA Guarantee',
            'Custom AI Training',
            'White-label Options',
            'Advanced Security',
        ],
        cta: 'Contact Sales',
        highlighted: false,
    },
];

const PricingPage = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    const handleCtaClick = (tier) => {
        if (tier.name === 'Enterprise') {
            window.location.href = 'mailto:sales@vitisiti.com?subject=Enterprise%20Inquiry';
        } else if (currentUser) {
            navigate('/dashboard');
        } else {
            navigate('/login');
        }
    };

    return (
        <div className="pricing-page">
            <LandingNavigation />

            {/* Hero */}
            <section className="pricing-hero">
                <h1 className="pricing-hero-title">Simple, Transparent Pricing</h1>
                <p className="pricing-hero-subtitle">
                    Choose the plan that fits your needs. All plans include a free trial.
                </p>
            </section>

            {/* Pricing Cards */}
            <div className="pricing-grid">
                {pricingTiers.map((tier) => (
                    <div
                        key={tier.name}
                        className={`pricing-card ${tier.highlighted ? 'highlighted' : ''}`}
                    >
                        {tier.highlighted && (
                            <div className="pricing-card-badge">Most Popular</div>
                        )}
                        <h3 className="pricing-card-name">{tier.name}</h3>
                        <p className="pricing-card-description">{tier.description}</p>
                        <div className="pricing-card-price">
                            <span className="pricing-card-price-value">{tier.price}</span>
                            {tier.price !== 'Contact Us' && tier.price !== 'TBA' && (
                                <span className="pricing-card-price-period">/month</span>
                            )}
                        </div>
                        <ul className="pricing-card-features">
                            {tier.features.map((feature) => (
                                <li key={feature}>
                                    <Check size={16} />
                                    {feature}
                                </li>
                            ))}
                        </ul>
                        <button
                            onClick={() => handleCtaClick(tier)}
                            className={`btn ${tier.highlighted ? 'btn-primary' : 'btn-secondary'}`}
                        >
                            {tier.cta}
                            <ArrowRight size={18} />
                        </button>
                    </div>
                ))}
            </div>

            {/* FAQ Section */}
            <section
                style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    padding: 'var(--space-12) var(--space-6) var(--space-20)',
                }}
            >
                <h2
                    style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 'var(--font-size-2xl)',
                        fontWeight: 700,
                        textAlign: 'center',
                        marginBottom: 'var(--space-8)',
                        color: 'var(--color-text-primary)',
                    }}
                >
                    Frequently Asked Questions
                </h2>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-4)',
                    }}
                >
                    <div
                        style={{
                            background: 'var(--color-surface)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--space-5)',
                        }}
                    >
                        <h4
                            style={{
                                fontWeight: 600,
                                marginBottom: 'var(--space-2)',
                                color: 'var(--color-text-primary)',
                            }}
                        >
                            When will pricing be available?
                        </h4>
                        <p
                            style={{
                                color: 'var(--color-text-secondary)',
                                fontSize: 'var(--font-size-sm)',
                                margin: 0,
                            }}
                        >
                            We're currently finalizing our pricing structure. Join the waitlist
                            to be notified when pricing is announced and receive early-bird discounts.
                        </p>
                    </div>
                    <div
                        style={{
                            background: 'var(--color-surface)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--space-5)',
                        }}
                    >
                        <h4
                            style={{
                                fontWeight: 600,
                                marginBottom: 'var(--space-2)',
                                color: 'var(--color-text-primary)',
                            }}
                        >
                            Is there a free trial?
                        </h4>
                        <p
                            style={{
                                color: 'var(--color-text-secondary)',
                                fontSize: 'var(--font-size-sm)',
                                margin: 0,
                            }}
                        >
                            Yes! All plans include a free trial period so you can explore
                            the platform before committing. No credit card required.
                        </p>
                    </div>
                    <div
                        style={{
                            background: 'var(--color-surface)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--space-5)',
                        }}
                    >
                        <h4
                            style={{
                                fontWeight: 600,
                                marginBottom: 'var(--space-2)',
                                color: 'var(--color-text-primary)',
                            }}
                        >
                            Can I change plans later?
                        </h4>
                        <p
                            style={{
                                color: 'var(--color-text-secondary)',
                                fontSize: 'var(--font-size-sm)',
                                margin: 0,
                            }}
                        >
                            Absolutely. You can upgrade or downgrade your plan at any time.
                            Changes take effect at the start of your next billing cycle.
                        </p>
                    </div>
                    <div
                        style={{
                            background: 'var(--color-surface)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--space-5)',
                        }}
                    >
                        <h4
                            style={{
                                fontWeight: 600,
                                marginBottom: 'var(--space-2)',
                                color: 'var(--color-text-primary)',
                            }}
                        >
                            What payment methods do you accept?
                        </h4>
                        <p
                            style={{
                                color: 'var(--color-text-secondary)',
                                fontSize: 'var(--font-size-sm)',
                                margin: 0,
                            }}
                        >
                            We accept all major credit cards, PayPal, and bank transfers for
                            Enterprise plans. All payments are processed securely.
                        </p>
                    </div>
                </div>
            </section>

            <LandingFooter />
        </div>
    );
};

export default PricingPage;
